import React, { useState } from "react";
import { Switch, SwitchField } from "../../../../design/switch";
import { Label } from "../../../../design/fieldset";
import { useContact } from "../../../../contexts/ContactContext";
import DeleteParentContactButton from "./DeleteContactButton";
import { Contact } from "@visitproof/types";
import { PhoneIcon, EnvelopeIcon } from "@heroicons/react/16/solid";

interface Props {
  contact: Contact;
}

const ParentDetailContactListItem: React.FC<Props> = ({ contact }) => {
  const { updateContact } = useContact();
  const [isEmergencyContact, setIsEmergencyContact] = useState(
    contact.isEmergencyContact,
  );
  const [isVisibleToSupervisor, setIsVisibleToSupervisor] = useState(
    contact.isVisibleToSupervisor,
  );

  const handleIsVisibleToSupervisorChange = async () => {
    try {
      updateContact(contact.id, {
        ...contact,
        isVisibleToSupervisor: !isVisibleToSupervisor,
      });
    } catch (error) {
      console.error("Error updating contact:", error);
      alert("Failed to update contact. Please try again later.");
    }
    setIsVisibleToSupervisor(!isVisibleToSupervisor);
  };

  const handleIsEmergencyContactChange = async () => {
    try {
      updateContact(contact.id, {
        ...contact,
        isEmergencyContact: !isEmergencyContact,
      });
    } catch (error) {
      console.error("Error updating contact:", error);
      alert("Failed to update contact. Please try again later.");
    }
    setIsEmergencyContact(!isEmergencyContact);
  };

  const fullName = `${contact.firstName} ${contact.lastName}`.trim();

  return (
    <div
      className={`p-3 rounded-lg border ${
        isEmergencyContact
          ? "bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800"
          : "bg-zinc-50 border-zinc-200 dark:bg-zinc-800/50 dark:border-zinc-700"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-zinc-900 dark:text-white truncate">
              {fullName}
            </span>
            {isEmergencyContact && (
              <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-red-700 bg-red-100 rounded dark:bg-red-900/50 dark:text-red-300">
                Emergency
              </span>
            )}
          </div>
          {contact.role && (
            <p className="text-sm text-zinc-500 dark:text-zinc-400">{contact.role}</p>
          )}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-600 dark:text-zinc-400">
            {contact.phoneNumber && (
              <span className="flex items-center gap-1">
                <PhoneIcon className="w-3.5 h-3.5" />
                {contact.phoneNumber}
              </span>
            )}
            {contact.email && (
              <span className="flex items-center gap-1 truncate">
                <EnvelopeIcon className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{contact.email}</span>
              </span>
            )}
          </div>
        </div>
        <DeleteParentContactButton contact={contact} />
      </div>
      <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-zinc-700 flex flex-wrap gap-4">
        <SwitchField>
          <Switch
            color="red"
            checked={isEmergencyContact}
            onChange={handleIsEmergencyContactChange}
          />
          <Label className="text-xs">Emergency Contact</Label>
        </SwitchField>
        <SwitchField>
          <Switch
            checked={isVisibleToSupervisor}
            onChange={handleIsVisibleToSupervisorChange}
          />
          <Label className="text-xs">Visible to Supervisor</Label>
        </SwitchField>
      </div>
    </div>
  );
};

export default ParentDetailContactListItem;
